/**
 * /settings/tax — Tax jurisdiction + tax code management.
 *
 * Left panel: jurisdictions list (country/state/city). Right panel:
 * codes under the selected jurisdiction with type, rate, and recovery flag.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  IconButton, InputLabel, List, ListItemButton, ListItemText, MenuItem,
  Paper, Select, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import TaxIcon from "@mui/icons-material/ReceiptLong";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncIcon from "@mui/icons-material/Sync";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";

export const Route = createFileRoute("/_auth/settings/tax")({
  component: TaxSettingsPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface TaxJurisdiction {
  id: string;
  code: string;
  name: string;
  country: string;
  state: string | null;
  city: string | null;
}

interface TaxRate {
  id: string;
  effective_from: string;
  effective_to: string | null;
  rate: string;
}

interface TaxCode {
  id: string;
  code: string;
  name: string;
  jurisdiction_id: string;
  kind: string;
  is_recoverable: boolean;
  is_active: boolean;
  rates: TaxRate[];
}

interface TaxBulkRow {
  code: string;
  name: string;
  jurisdiction_code: string;
  kind: string;
  rate: number;
  effective_from: string;
  effective_to?: string | null;
  is_recoverable?: boolean;
}

interface TaxBulkImportResult {
  created_jurisdictions: number;
  created_codes: number;
  updated_codes: number;
  appended_rates: number;
  skipped: number;
  errors: string[];
}

interface TaxJarSyncResult {
  regions_fetched: number;
  jurisdictions_created: number;
  codes_created: number;
  codes_updated: number;
  rates_appended: number;
  errors: string[];
}

interface TaxRateOut {
  id: string;
  effective_from: string;
  effective_to: string | null;
  rate: string;
}

interface TaxCodeOut {
  id: string;
  code: string;
  name: string;
  jurisdiction_id: string;
  kind: string;
  is_recoverable: boolean;
  is_active: boolean;
  rates: TaxRate[];
}

// ── Query options ──────────────────────────────────────────────────────────
const jurisdictionsQueryOptions = () =>
  queryOptions({
    queryKey: ["tax-jurisdictions"],
    queryFn: () => api<TaxJurisdiction[]>("/api/tax/jurisdictions"),
  });

const taxCodesQueryOptions = () =>
  queryOptions({
    queryKey: ["tax-codes"],
    queryFn: () => api<TaxCode[]>("/api/tax/codes"),
  });

const TAX_KIND_COLORS: Record<string, "primary" | "secondary" | "info" | "warning" | "success" | "default"> = {
  sales: "primary", use: "secondary", vat: "info",
  gst: "success", hst: "success", withholding: "warning",
};

const BULK_IMPORT_EXAMPLE = `[{"code":"CA-SALES","name":"California Sales Tax","jurisdiction_code":"US-CA","kind":"sales","rate":8.25,"effective_from":"2026-01-01"}]`;

const TAXJAR_COUNTRY_OPTIONS = ["US", "CA", "GB", "EU", "AU"] as const;

// ── Sub-components ─────────────────────────────────────────────────────────

/** Inline rate history row, shown when a tax code row is expanded. */
function RateHistorySection({
  codeId,
  rates,
  onRateAdded,
}: {
  codeId: string;
  rates: TaxRate[];
  onRateAdded: () => void;
}) {
  const [appendOpen, setAppendOpen] = useState(false);
  const [newRate, setNewRate] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: "",
    rate: "",
  });

  const qc = useQueryClient();

  const appendRateMut = useMutation({
    mutationFn: (p: typeof newRate) =>
      api<TaxRateOut>(`/api/tax/codes/${codeId}/rates`, {
        method: "POST",
        body: JSON.stringify({
          effective_from: p.effective_from,
          effective_to: p.effective_to || null,
          rate: parseFloat(p.rate),
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-codes"] });
      setAppendOpen(false);
      setNewRate({ effective_from: new Date().toISOString().slice(0, 10), effective_to: "", rate: "" });
      onRateAdded();
    },
  });

  const sorted = [...rates].sort(
    (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
  );

  return (
    <Box sx={{ pl: 6, pr: 2, pb: 2, bgcolor: "action.hover" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
            <TableCell>Period</TableCell>
            <TableCell>Rate</TableCell>
            <TableCell align="right">
              <Button size="small" startIcon={<AddIcon />} onClick={() => setAppendOpen(true)}>
                Add New Rate
              </Button>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ color: "text.secondary", fontSize: 12 }}>No rates recorded.</TableCell>
            </TableRow>
          )}
          {sorted.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                {r.effective_from} → {r.effective_to ?? "open"}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {parseFloat(r.rate).toFixed(3)}%
              </TableCell>
              <TableCell />
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Append rate dialog */}
      <Dialog open={appendOpen} onClose={() => setAppendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Rate</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Rate (%)" type="number" size="small" required
            value={newRate.rate}
            onChange={(e) => setNewRate((p) => ({ ...p, rate: e.target.value }))}
            inputProps={{ step: "0.001" }}
          />
          <TextField
            label="Effective From" type="date" size="small" required
            InputLabelProps={{ shrink: true }}
            value={newRate.effective_from}
            onChange={(e) => setNewRate((p) => ({ ...p, effective_from: e.target.value }))}
          />
          <TextField
            label="Effective To (optional)" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={newRate.effective_to}
            onChange={(e) => setNewRate((p) => ({ ...p, effective_to: e.target.value }))}
          />
          {appendRateMut.error && <Alert severity="error">{String(appendRateMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppendOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={appendRateMut.isPending || !newRate.rate}
            onClick={() => appendRateMut.mutate(newRate)}>
            {appendRateMut.isPending ? "Saving…" : "Save Rate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
function TaxSettingsPage() {
  const qc = useQueryClient();
  const jurisdictionsQ = useQuery(jurisdictionsQueryOptions());
  const codesQ = useQuery(taxCodesQueryOptions());

  const [selectedJurId, setSelectedJurId] = useState<string | null>(null);
  const [jurOpen, setJurOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [taxjarOpen, setTaxjarOpen] = useState(false);

  // Expanded row tracking
  const [expandedCodeIds, setExpandedCodeIds] = useState<Set<string>>(new Set());

  const [newJur, setNewJur] = useState({ code: "", name: "", country: "", state: "", city: "" });
  const [newCode, setNewCode] = useState({
    code: "", name: "", kind: "sales", is_recoverable: false,
    jurisdiction_id: "",
    effective_from: new Date().toISOString().slice(0, 10),
    rate: "",
  });

  // Bulk import state
  const [bulkJson, setBulkJson] = useState("");
  const [bulkJsonError, setBulkJsonError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<TaxBulkImportResult | null>(null);

  // TaxJar sync state
  const [taxjarCountries, setTaxjarCountries] = useState<Set<string>>(new Set(["US"]));
  const [taxjarEffectiveFrom, setTaxjarEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [taxjarResult, setTaxjarResult] = useState<TaxJarSyncResult | null>(null);

  const addJurMut = useMutation({
    mutationFn: (p: typeof newJur) =>
      api<TaxJurisdiction>("/api/tax/jurisdictions", {
        method: "POST",
        body: JSON.stringify({ ...p, state: p.state || null, city: p.city || null }),
      }),
    onSuccess: (jur) => {
      void qc.invalidateQueries({ queryKey: ["tax-jurisdictions"] });
      setJurOpen(false);
      setSelectedJurId(jur.id);
      setNewJur({ code: "", name: "", country: "", state: "", city: "" });
    },
  });

  const addCodeMut = useMutation({
    mutationFn: (p: typeof newCode) =>
      api<TaxCode>("/api/tax/codes", {
        method: "POST",
        body: JSON.stringify({
          code: p.code, name: p.name, kind: p.kind,
          is_recoverable: p.is_recoverable,
          jurisdiction_id: p.jurisdiction_id,
          rates: p.rate ? [{ effective_from: p.effective_from, rate: parseFloat(p.rate) }] : [],
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-codes"] });
      setCodeOpen(false);
      setNewCode({ code: "", name: "", kind: "sales", is_recoverable: false, jurisdiction_id: "", effective_from: new Date().toISOString().slice(0, 10), rate: "" });
    },
  });

  const bulkImportMut = useMutation({
    mutationFn: (rows: TaxBulkRow[]) =>
      api<TaxBulkImportResult>("/api/tax/import/bulk", {
        method: "POST",
        body: JSON.stringify({ rows }),
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["tax-codes"] });
      void qc.invalidateQueries({ queryKey: ["tax-jurisdictions"] });
      setBulkResult(result);
    },
  });

  const taxjarSyncMut = useMutation({
    mutationFn: (params: { country_codes: string[]; effective_from?: string }) =>
      api<TaxJarSyncResult>("/api/tax/import/taxjar", {
        method: "POST",
        body: JSON.stringify(params),
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["tax-codes"] });
      void qc.invalidateQueries({ queryKey: ["tax-jurisdictions"] });
      setTaxjarResult(result);
    },
  });

  const patchCodeMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { is_active?: boolean; name?: string } }) =>
      api<TaxCodeOut>(`/api/tax/codes/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-codes"] });
    },
  });

  const jurisdictions = jurisdictionsQ.data ?? [];
  const allCodes = codesQ.data ?? [];
  const selectedJur = jurisdictions.find((j) => j.id === selectedJurId);
  const filteredCodes = selectedJurId
    ? allCodes.filter((c) => c.jurisdiction_id === selectedJurId)
    : allCodes;

  function toggleExpand(id: string) {
    setExpandedCodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleBulkImport() {
    setBulkJsonError(null);
    setBulkResult(null);
    let rows: TaxBulkRow[];
    try {
      rows = JSON.parse(bulkJson) as TaxBulkRow[];
    } catch (e) {
      setBulkJsonError("Invalid JSON: " + String(e));
      return;
    }
    if (!Array.isArray(rows)) {
      setBulkJsonError("Expected a JSON array of objects.");
      return;
    }
    bulkImportMut.mutate(rows);
  }

  function handleTaxjarSync() {
    setTaxjarResult(null);
    taxjarSyncMut.mutate({
      country_codes: Array.from(taxjarCountries),
      effective_from: taxjarEffectiveFrom || undefined,
    });
  }

  function toggleTaxjarCountry(code: string) {
    setTaxjarCountries((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <TaxIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Tax Configuration</Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 160px)" }}>
        {/* Left: jurisdictions */}
        <Paper variant="outlined" sx={{ width: 280, display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" fontWeight={600}>Jurisdictions</Typography>
            <IconButton size="small" onClick={() => setJurOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <List dense sx={{ flex: 1, overflow: "auto" }}>
            <ListItemButton
              selected={selectedJurId === null}
              onClick={() => setSelectedJurId(null)}
            >
              <ListItemText primary="All jurisdictions" primaryTypographyProps={{ fontSize: 13 }} />
            </ListItemButton>
            <Divider />
            {jurisdictions.map((j) => (
              <ListItemButton
                key={j.id}
                selected={j.id === selectedJurId}
                onClick={() => setSelectedJurId(j.id)}
              >
                <ListItemText
                  primary={j.name}
                  secondary={[j.country, j.state, j.city].filter(Boolean).join(" · ")}
                  primaryTypographyProps={{ fontSize: 13 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </ListItemButton>
            ))}
            {jurisdictions.length === 0 && (
              <Box sx={{ p: 2, color: "text.secondary", fontSize: 13 }}>
                No jurisdictions yet
              </Box>
            )}
          </List>
        </Paper>

        {/* Right: tax codes */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedJur ? `Tax codes — ${selectedJur.name}` : "All tax codes"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small" variant="outlined" startIcon={<UploadFileIcon />}
                onClick={() => { setBulkResult(null); setBulkJsonError(null); setBulkJson(""); setBulkOpen(true); }}
              >
                Bulk Import
              </Button>
              <Button
                size="small" variant="outlined" startIcon={<SyncIcon />}
                onClick={() => { setTaxjarResult(null); setTaxjarOpen(true); }}
              >
                Sync TaxJar
              </Button>
              <Button
                size="small" variant="contained" startIcon={<AddIcon />}
                onClick={() => {
                  setNewCode((p) => ({ ...p, jurisdiction_id: selectedJurId ?? "" }));
                  setCodeOpen(true);
                }}
              >
                Add Code
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Current Rate</TableCell>
                  <TableCell>Recoverable</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No tax codes
                      {selectedJur ? ` for ${selectedJur.name}` : ""}. Add one above.
                    </TableCell>
                  </TableRow>
                )}
                {filteredCodes.map((c) => {
                  const currentRate = c.rates.length > 0
                    ? c.rates[c.rates.length - 1].rate
                    : null;
                  const expanded = expandedCodeIds.has(c.id);
                  return (
                    <>
                      <TableRow key={c.id} hover sx={{ "& td": { verticalAlign: "middle" } }}>
                        <TableCell sx={{ p: 0.5 }}>
                          <IconButton size="small" onClick={() => toggleExpand(c.id)}>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{c.code}</TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={c.kind.toUpperCase()}
                            size="small"
                            color={TAX_KIND_COLORS[c.kind] ?? "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {currentRate != null ? `${parseFloat(currentRate).toFixed(3)}%` : "—"}
                        </TableCell>
                        <TableCell>
                          {c.is_recoverable ? (
                            <Chip label="Yes" size="small" color="success" variant="outlined" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">No</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={c.is_active ? "Active" : "Inactive"} size="small"
                            color={c.is_active ? "success" : "default"} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color={c.is_active ? "warning" : "success"}
                            disabled={patchCodeMut.isPending}
                            onClick={() => patchCodeMut.mutate({ id: c.id, patch: { is_active: !c.is_active } })}
                          >
                            {c.is_active ? "Deactivate" : "Reactivate"}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow key={`${c.id}-history`}>
                        <TableCell colSpan={8} sx={{ p: 0, border: expanded ? undefined : "none" }}>
                          <Collapse in={expanded} unmountOnExit>
                            <RateHistorySection
                              codeId={c.id}
                              rates={c.rates}
                              onRateAdded={() => {}}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Add jurisdiction dialog */}
      <Dialog open={jurOpen} onClose={() => setJurOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Tax Jurisdiction</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Code" size="small" required value={newJur.code}
            onChange={(e) => setNewJur((p) => ({ ...p, code: e.target.value }))} />
          <TextField label="Name" size="small" required value={newJur.name}
            onChange={(e) => setNewJur((p) => ({ ...p, name: e.target.value }))} />
          <TextField label="Country" size="small" required value={newJur.country}
            onChange={(e) => setNewJur((p) => ({ ...p, country: e.target.value }))}
            placeholder="US, CA, etc." />
          <TextField label="State / Province" size="small" value={newJur.state}
            onChange={(e) => setNewJur((p) => ({ ...p, state: e.target.value }))} />
          <TextField label="City" size="small" value={newJur.city}
            onChange={(e) => setNewJur((p) => ({ ...p, city: e.target.value }))} />
          {addJurMut.error && <Alert severity="error">{String(addJurMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJurOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addJurMut.isPending}
            onClick={() => addJurMut.mutate(newJur)}>
            {addJurMut.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add tax code dialog */}
      <Dialog open={codeOpen} onClose={() => setCodeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Tax Code</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Code" size="small" required value={newCode.code}
            onChange={(e) => setNewCode((p) => ({ ...p, code: e.target.value }))} />
          <TextField label="Name" size="small" required value={newCode.name}
            onChange={(e) => setNewCode((p) => ({ ...p, name: e.target.value }))} />
          <FormControl size="small" fullWidth>
            <InputLabel>Jurisdiction</InputLabel>
            <Select value={newCode.jurisdiction_id} label="Jurisdiction"
              onChange={(e) => setNewCode((p) => ({ ...p, jurisdiction_id: e.target.value }))}>
              {jurisdictions.map((j) => (
                <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={newCode.kind} label="Type"
              onChange={(e) => setNewCode((p) => ({ ...p, kind: e.target.value }))}>
              {["sales", "use", "vat", "gst", "hst", "withholding"].map((k) => (
                <MenuItem key={k} value={k}>{k.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Rate (%)" type="number" size="small" sx={{ flex: 1 }}
              value={newCode.rate}
              onChange={(e) => setNewCode((p) => ({ ...p, rate: e.target.value }))}
              inputProps={{ step: "0.001" }} />
            <TextField label="Effective From" type="date" size="small" sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
              value={newCode.effective_from}
              onChange={(e) => setNewCode((p) => ({ ...p, effective_from: e.target.value }))} />
          </Box>
          <FormControlLabel
            control={<Switch checked={newCode.is_recoverable}
              onChange={(e) => setNewCode((p) => ({ ...p, is_recoverable: e.target.checked }))} />}
            label="Recoverable (e.g. input tax credit)"
          />
          {addCodeMut.error && <Alert severity="error">{String(addCodeMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addCodeMut.isPending}
            onClick={() => addCodeMut.mutate(newCode)}>
            {addCodeMut.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Import Tax Codes</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Paste a JSON array of tax code rows. Each row must include{" "}
            <code>code</code>, <code>name</code>, <code>jurisdiction_code</code>,{" "}
            <code>kind</code>, <code>rate</code>, and <code>effective_from</code>.
          </Typography>
          <TextField
            label="JSON Input"
            multiline
            rows={8}
            fullWidth
            value={bulkJson}
            onChange={(e) => { setBulkJson(e.target.value); setBulkJsonError(null); }}
            placeholder={BULK_IMPORT_EXAMPLE}
            error={!!bulkJsonError}
            helperText={bulkJsonError ?? undefined}
            inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
          />
          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Example:
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
              {BULK_IMPORT_EXAMPLE}
            </Typography>
          </Box>
          {bulkImportMut.error && <Alert severity="error">{String(bulkImportMut.error)}</Alert>}
          {bulkResult && (
            <Alert severity={bulkResult.errors.length > 0 ? "warning" : "success"}>
              <Typography variant="body2" fontWeight={600}>Import complete</Typography>
              <Typography variant="body2">
                Codes created: {bulkResult.created_codes} · Updated: {bulkResult.updated_codes} ·
                Rates appended: {bulkResult.appended_rates} · Skipped: {bulkResult.skipped}
              </Typography>
              {bulkResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {bulkResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="error.main">{e}</Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={bulkImportMut.isPending || !bulkJson.trim()}
            onClick={handleBulkImport}
          >
            {bulkImportMut.isPending ? "Importing…" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TaxJar Sync dialog */}
      <Dialog open={taxjarOpen} onClose={() => setTaxjarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sync from TaxJar</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select regions to sync. TaxJar rates will be fetched and upserted into your tax codes.
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
              Country / Region
            </Typography>
            {TAXJAR_COUNTRY_OPTIONS.map((cc) => (
              <FormControlLabel
                key={cc}
                control={
                  <Checkbox
                    size="small"
                    checked={taxjarCountries.has(cc)}
                    onChange={() => toggleTaxjarCountry(cc)}
                  />
                }
                label={cc}
              />
            ))}
          </Box>
          <TextField
            label="Effective From (optional)"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={taxjarEffectiveFrom}
            onChange={(e) => setTaxjarEffectiveFrom(e.target.value)}
          />
          {taxjarSyncMut.error && <Alert severity="error">{String(taxjarSyncMut.error)}</Alert>}
          {taxjarResult && (
            <Alert severity={taxjarResult.errors.length > 0 ? "warning" : "success"}>
              <Typography variant="body2" fontWeight={600}>Sync complete</Typography>
              <Typography variant="body2">
                Regions fetched: {taxjarResult.regions_fetched} ·
                Jurisdictions: {taxjarResult.jurisdictions_created} created ·
                Codes: {taxjarResult.codes_created} created, {taxjarResult.codes_updated} updated ·
                Rates: {taxjarResult.rates_appended} appended
              </Typography>
              {taxjarResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {taxjarResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="error.main">{e}</Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxjarOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            disabled={taxjarSyncMut.isPending || taxjarCountries.size === 0}
            onClick={handleTaxjarSync}
          >
            {taxjarSyncMut.isPending ? "Syncing…" : "Sync from TaxJar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
